// === Admin: Companies ===
// Full CRUD page — list, add, edit, soft-delete, restore.
// Ported from Click2Gro PHP (CompaniesController.php / company table)
// Columns: company_name, email, contact, mobile, address, plan, status + Actions
// Tabs: Active / Deleted

import { useEffect, useState, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Divider,
  Stack,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Switch,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  RestoreFromTrash as RestoreIcon,
  Business as BusinessIcon,
  PeopleAlt as PeopleAltIcon,
  CalendarMonth as CalendarMonthIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import Autocomplete from '@mui/material/Autocomplete';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';
import { UkAddressFields, type UkAddressValue } from '../components/UkAddressFields';
import { UkWeatherPreview } from '../components/UkWeatherPreview';
import { findCountry, formatTaxPercent, useCountries } from '../lib/countries';

// ---------------------------------------------------------------------------
// Lookup types
// ---------------------------------------------------------------------------
interface StateOption  { readonly state_id: number; readonly state: string; }
interface SuburbOption {
  readonly id: number;
  readonly suburb: string;
  readonly postcode: string;
  readonly country: string;
  readonly lat: number | null;
  readonly lon: number | null;
}

// ---------------------------------------------------------------------------
// Plan helpers
// ---------------------------------------------------------------------------
const PLAN_LABELS: Readonly<Record<number, string>> = { 0: 'Free', 1: 'Essential', 2: 'Standard', 3: 'Premium' };
type PlanColor = 'default' | 'info' | 'success' | 'warning';

// Fixed set of free-trial durations (days) offered when onboarding a company.
const TRIAL_DAY_OPTIONS: readonly number[] = [7, 14, 30, 60, 90, 180, 365];

// Agronomist seats a company can be allocated (0–10). Ported from the legacy
// Click2Gro Client Management dropdown. 0 = feature off / no agronomist seats.
const AGRONOMIST_SEAT_OPTIONS: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Paid-plan catalogue with LIVE prices from Stripe (GET /admin/companies/plans).
interface PlanCatalogItem {
  readonly plan_type: 1 | 2 | 3;
  readonly name:      string;
  readonly price:     number | null;
  readonly currency:  string;
  readonly interval:  string | null;
  readonly live:      boolean;
}

// "Essential ($190/month)" from a live Stripe price. Falls back gracefully when
// Stripe omits an amount.
function planOptionLabel(p: PlanCatalogItem): string {
  if (p.price == null) return p.name;
  const amount = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: (p.currency || 'aud').toUpperCase(),
    minimumFractionDigits: Number.isInteger(p.price) ? 0 : 2,
  }).format(p.price);
  return `${p.name} (${amount}/${p.interval ?? 'month'})`;
}

// ---------------------------------------------------------------------------
// Program-data pickers (Crop Types / Suppliers / Chemicals / …)
// Global reference options come from GET /admin/companies/program-options.
// ---------------------------------------------------------------------------
interface ProgramOption { readonly id: number; readonly name: string; }

interface ProgramOptions {
  readonly cropTypes:   ProgramOption[];
  readonly suppliers:   ProgramOption[];
  readonly chemicals:   ProgramOption[];
  readonly fertilizers: ProgramOption[];
  readonly soilTests:   ProgramOption[];
  readonly waterRates:  ProgramOption[];
}

const EMPTY_PROGRAM_OPTIONS: ProgramOptions = {
  cropTypes: [], suppliers: [], chemicals: [], fertilizers: [], soilTests: [], waterRates: [],
};

// Each category maps to a tab label, its options bucket, and the FormState
// field holding the selected IDs. Drives both the tabs and the payload.
type ProgramOptionsKey = keyof ProgramOptions;
type ProgramIdField =
  | 'crop_type_ids' | 'supplier_ids' | 'chemical_ids'
  | 'fertilizer_ids' | 'soil_test_lab_ids' | 'water_rate_ids';

const PROGRAM_CATEGORIES: ReadonlyArray<{
  readonly label: string;
  readonly optionsKey: ProgramOptionsKey;
  readonly formKey: ProgramIdField;
}> = [
  { label: 'Crop Types',  optionsKey: 'cropTypes',   formKey: 'crop_type_ids' },
  { label: 'Suppliers',   optionsKey: 'suppliers',   formKey: 'supplier_ids' },
  { label: 'Chemicals',   optionsKey: 'chemicals',   formKey: 'chemical_ids' },
  { label: 'Fertilizers', optionsKey: 'fertilizers', formKey: 'fertilizer_ids' },
  { label: 'Soil Tests',  optionsKey: 'soilTests',   formKey: 'soil_test_lab_ids' },
  { label: 'Water Rates', optionsKey: 'waterRates',  formKey: 'water_rate_ids' },
];

// Create-mode final review tab — sits after the program-data pickers (tabs 3..8).
const SUMMARY_TAB = 3 + PROGRAM_CATEGORIES.length;

const ONBOARDING_LABELS: Readonly<Record<string, string>> = {
  pay_first:  'Pay-first',
  free_trial: 'Free trial',
  internal:   'Internal / staff',
};

const PICKER_PAGE_SIZE = 10;

// Dual-pane picker: searchable available list (left) + selected items (right).
// Mirrors the web Program Settings page, scoped to one category.
function ProgramDataPicker({
  label,
  options,
  value,
  onChange,
  loading,
}: {
  readonly label: string;
  readonly options: ProgramOption[];
  readonly value: number[];
  readonly onChange: (ids: number[]) => void;
  readonly loading: boolean;
}) {
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [rightPage, setRightPage] = useState(1);

  const selectedSet = new Set(value);
  const byId = new Map(options.map((o) => [o.id, o]));

  const leftFiltered = options.filter((o) =>
    leftSearch.trim() === '' ? true : o.name.toLowerCase().includes(leftSearch.toLowerCase()),
  );

  const selectedItems = value
    .map((id) => byId.get(id) ?? { id, name: `#${id}` })
    .filter((o) => rightSearch.trim() === '' ? true : o.name.toLowerCase().includes(rightSearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const totalPages = Math.max(1, Math.ceil(selectedItems.length / PICKER_PAGE_SIZE));
  const page = Math.min(rightPage, totalPages);
  const pageItems = selectedItems.slice((page - 1) * PICKER_PAGE_SIZE, page * PICKER_PAGE_SIZE);

  const add = (id: number) => { if (!selectedSet.has(id)) onChange([...value, id]); };
  const remove = (id: number) => onChange(value.filter((v) => v !== id));

  return (
    <Grid container spacing={2}>
      {/* LEFT: available options */}
      <Grid item xs={12} md={6}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary' }}>
          Add {label}
        </Typography>
        <TextField
          placeholder={`Search ${label.toLowerCase()}…`}
          size="small"
          fullWidth
          value={leftSearch}
          onChange={(e) => setLeftSearch(e.target.value)}
          sx={{ mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <Paper variant="outlined" sx={{ mt: 1, maxHeight: 360, minHeight: 200, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} /></Box>
          ) : leftFiltered.length === 0 ? (
            <Typography variant="caption" sx={{ display: 'block', p: 2, color: 'text.secondary' }}>
              {options.length === 0 ? 'No items available' : 'No matches'}
            </Typography>
          ) : (
            <List dense disablePadding>
              {leftFiltered.map((o) => {
                const added = selectedSet.has(o.id);
                return (
                  <ListItem
                    key={o.id}
                    divider
                    secondaryAction={added ? (
                      <Typography sx={{ color: 'success.main', fontWeight: 600, fontSize: 12 }}>Added</Typography>
                    ) : (
                      <Button size="small" startIcon={<AddIcon />} onClick={() => add(o.id)}>Add</Button>
                    )}
                    sx={{ opacity: added ? 0.6 : 1 }}
                  >
                    <ListItemText primaryTypographyProps={{ fontSize: 14 }} primary={o.name} />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      </Grid>

      {/* RIGHT: selected items */}
      <Grid item xs={12} md={6}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary' }}>
          Selected {label} ({value.length})
        </Typography>
        <TextField
          placeholder="Search items…"
          size="small"
          fullWidth
          value={rightSearch}
          onChange={(e) => { setRightSearch(e.target.value); setRightPage(1); }}
          sx={{ mt: 1 }}
        />
        <Paper variant="outlined" sx={{ mt: 1, maxHeight: 360, minHeight: 200, overflow: 'auto' }}>
          {value.length === 0 ? (
            <Typography variant="caption" sx={{ display: 'block', p: 2, color: 'text.secondary' }}>
              No {label.toLowerCase()} selected yet.
            </Typography>
          ) : pageItems.length === 0 ? (
            <Typography variant="caption" sx={{ display: 'block', p: 2, color: 'text.secondary' }}>
              No matching items.
            </Typography>
          ) : (
            <List dense disablePadding>
              {pageItems.map((o) => (
                <ListItem
                  key={o.id}
                  divider
                  secondaryAction={
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(o.id)}>Remove</Button>
                  }
                >
                  <ListItemText primaryTypographyProps={{ fontSize: 14 }} primary={o.name} />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Pagination size="small" count={totalPages} page={page} onChange={(_, p) => setRightPage(p)} />
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
const PLAN_COLORS: Readonly<Record<number, PlanColor>> = { 0: 'default', 1: 'info', 2: 'success', 3: 'warning' };

// Stripe subscription statuses — mirrors Stripe API docs
type StripeStatusColor = 'success' | 'warning' | 'error' | 'info' | 'default';
const STRIPE_STATUS_COLORS: Readonly<Record<string, StripeStatusColor>> = {
  active:             'success',
  trialing:           'info',
  past_due:           'warning',
  canceled:           'error',
  unpaid:             'error',
  incomplete_expired: 'error',
  incomplete:         'default',
  paused:             'default',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface CompanyRow {
  readonly id:                    string;
  readonly company_name:          string;
  readonly email_address:         string | null;
  readonly phone_no:              string | null;
  readonly mobile_number:         string | null;
  readonly street:                string | null;
  readonly suburb:                string | null;
  readonly suburb_name:           string | null;
  readonly postcode:              string | null;
  readonly state:                 string | null;
  readonly state_name:            string | null;
  readonly country:               string | null;
  readonly country_code:          string | null;
  readonly currency_code:         string | null;
  readonly tax_label:             string | null;
  readonly tax_rate:              number | string | null;
  readonly payroll_enabled?:      boolean | null;
  readonly lat:                   number | null;
  readonly lon:                   number | null;
  readonly abn:                   string | null;
  readonly first_name:            string | null;
  readonly last_name:             string | null;
  readonly title:                 string | null;
  readonly status:                string | null;
  readonly paid_sub:              boolean | null;
  readonly customer_id:           string | null;
  readonly subscription_id:       string | null;
  readonly logo_url:              string | null;
  readonly sales_tax_default:     string | null;
  readonly setup_wizard:          number | null;
  readonly plan_type:             number;
  readonly agronomist:            number | null;
  readonly block_broadacre:       boolean | null;
  readonly block_fixed_planting:  boolean | null;
  readonly block_glasshouse:      boolean | null;
  readonly block_pivot:           boolean | null;
  readonly block_fixed_irrigation: boolean | null;
  readonly stripe_status:         string | null;
  readonly stripe_plan_name:      string | null;
  readonly stripe_last_payment:    string | null;
  readonly admin_email:           string | null;
  readonly admin_user_id:         string | null;
  readonly subscription_state:    string | null;
  readonly onboarding_mode:       string | null;
  readonly trial_ends_at:         string | null;
  readonly activated_at:          string | null;
  readonly created_at:            string | null;
  readonly updated_at:            string | null;
  readonly created_by:            string | null;
  readonly updated_by:            string | null;
  readonly deleted_at:            string | null;
  readonly decommissioned_at:      string | null;
}

interface FormState {
  company_name:          string;
  email_address:         string;
  phone_no:              string;
  mobile_number:         string;
  street:                string;
  suburb:                string;
  postcode:              string;
  state:                 string;
  country:               string;
  /** A public.countries code (lib/countries.ts). */
  country_code:          string;
  /** Company currency + sales tax — defaults from the country, editable. */
  currency_code:         string;
  tax_label:             string;
  /** Percent as typed, e.g. "10" for 10%. Sent to the API as a fraction. */
  tax_rate_pct:          string;
  /** Payroll + Xero/MYOB. Off = Timesheets only (UK companies by default). */
  payroll_enabled:       boolean;
  lat:                   string;
  lon:                   string;
  abn:                   string;
  first_name:            string;
  last_name:             string;
  title:                 string;
  status:                string;
  paid_sub:              boolean;
  customer_id:           string;
  subscription_id:       string;
  logo_url:              string;
  sales_tax_default:     string;
  setup_wizard:          string;
  plan_type:             number;
  agronomist:            number;   // agronomist seats allocated (0–10)
  block_broadacre:       boolean;
  block_fixed_planting:  boolean;
  block_glasshouse:      boolean;
  block_pivot:           boolean;
  block_fixed_irrigation: boolean;
  // New onboarding fields. Only sent when creating; ignored on edit.
  onboarding_mode:       'pay_first' | 'free_trial' | 'internal';
  trial_days:            number;
  // Read-only on edit; populated from server response.
  subscription_state:    string;
  trial_ends_at:         string;
  // Program-data selections (create-mode only) — arrays of reference IDs.
  crop_type_ids:         number[];
  supplier_ids:          number[];
  chemical_ids:          number[];
  fertilizer_ids:        number[];
  soil_test_lab_ids:     number[];
  water_rate_ids:        number[];
}

const DEFAULT_FORM: FormState = {
  company_name:          '',
  email_address:         '',
  phone_no:              '',
  mobile_number:         '',
  street:                '',
  suburb:                '',
  postcode:              '',
  state:                 '',
  country:               'AUSTRALIA',
  country_code:          'AU',
  currency_code:         'AUD',
  tax_label:             'GST',
  tax_rate_pct:          '10',
  payroll_enabled:       true,
  lat:                   '',
  lon:                   '',
  abn:                   '',
  first_name:            '',
  last_name:             '',
  title:                 '',
  status:                '',
  paid_sub:              false,
  customer_id:           '',
  subscription_id:       '',
  logo_url:              '',
  sales_tax_default:     '',
  setup_wizard:          '0',
  // Default to Essential so the Subscription dropdown's displayed plan matches the
  // actual value (it shows Essential for 0 but wouldn't pass validation until changed).
  plan_type:             1,
  agronomist:            0,
  block_broadacre:       false,
  block_fixed_planting:  false,
  block_glasshouse:      false,
  block_pivot:           false,
  block_fixed_irrigation: false,
  onboarding_mode:       'pay_first',
  trial_days:            14,
  subscription_state:    '',
  trial_ends_at:         '',
  crop_type_ids:         [],
  supplier_ids:          [],
  chemical_ids:          [],
  fertilizer_ids:        [],
  soil_test_lab_ids:     [],
  water_rate_ids:        [],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtAuDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).format(d);
}

function contactName(row: CompanyRow): string {
  return [row.first_name, row.last_name].filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Pay-first card entry (Stripe Elements)
// ---------------------------------------------------------------------------
// Renders the Stripe CardElement (card fields live inside Stripe's own iframe —
// raw card data never touches our code) and exposes an imperative
// createPaymentMethod() the parent form calls on save. Must be rendered inside
// an <Elements> provider so the Stripe hooks resolve.
export interface CardSectionHandle {
  createPaymentMethod: () => Promise<
    { ok: true; paymentMethodId: string } | { ok: false; error: string }
  >;
}

const CardSection = forwardRef<CardSectionHandle>((_props, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    async createPaymentMethod() {
      if (!stripe || !elements) {
        return { ok: false as const, error: 'Payment form is still loading — please wait a moment and try again.' };
      }
      const card = elements.getElement(CardElement);
      if (!card) {
        return { ok: false as const, error: 'Card field is not ready.' };
      }
      const { error, paymentMethod } = await stripe.createPaymentMethod({ type: 'card', card });
      if (error || !paymentMethod) {
        const msg = error?.message ?? 'Card details are invalid.';
        setCardError(msg);
        return { ok: false as const, error: msg };
      }
      setCardError(null);
      return { ok: true as const, paymentMethodId: paymentMethod.id };
    },
  }), [stripe, elements]);

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        Card details
      </Typography>
      <Box
        sx={{
          border: '1px solid',
          borderColor: cardError ? 'error.main' : 'rgba(255,255,255,0.23)',
          borderRadius: 1,
          px: 1.5,
          py: 1.75,
          bgcolor: 'background.paper',
        }}
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#ffffff',
                '::placeholder': { color: '#9aa0a6' },
              },
              invalid: { color: '#f44336' },
            },
          }}
          onChange={(e) => setCardError(e.error?.message ?? null)}
        />
      </Box>
      {cardError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {cardError}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        The card is charged immediately for the first billing period and saved for future renewals.
      </Typography>
    </Box>
  );
});
CardSection.displayName = 'CardSection';

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
export function CompaniesPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';
  const countries = useCountries();

  // ---- List tabs (Active / Deleted) ----
  const [listTab, setListTab] = useState<0 | 1>(0);
  const showDeleted = listTab === 1;

  // ---- List state ----
  const [rows, setRows] = useState<readonly CompanyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Stats ----
  const [activeCompaniesThisMonth, setActiveCompaniesThisMonth] = useState<number | null>(null);
  const [activeCompaniesThisWeek,  setActiveCompaniesThisWeek]  = useState<number | null>(null);
  const [avgActiveCompaniesYear,   setAvgActiveCompaniesYear]   = useState<number | null>(null);
  useEffect(() => {
    if (!token) return;
    void fetch(`${getApiUrl()}/api/v1/admin/companies/stats`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((body: { active_companies_this_month: number | null; active_companies_this_week: number | null; avg_active_companies_this_year: number | null }) => {
        setActiveCompaniesThisMonth(body.active_companies_this_month);
        setActiveCompaniesThisWeek(body.active_companies_this_week);
        setAvgActiveCompaniesYear(body.avg_active_companies_this_year);
      })
      .catch(() => { /* non-critical — leave as null */ });
  }, [token]);

  // ---- Plan catalogue (live prices from Stripe) ----
  const [planCatalog, setPlanCatalog] = useState<readonly PlanCatalogItem[]>([]);
  // Stripe publishable key (from the same /plans response) → mounts Elements for
  // the pay-first card entry. null until loaded / if Stripe isn't configured.
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  useEffect(() => {
    if (!token) return;
    void fetch(`${getApiUrl()}/api/v1/admin/companies/plans`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((body: { plans: PlanCatalogItem[]; publishableKey?: string | null }) => {
        setPlanCatalog(body.plans);
        setStripePublishableKey(body.publishableKey ?? null);
      })
      .catch(() => { /* non-critical — dropdown falls back to static labels */ });
  }, [token]);

  // Single Stripe.js instance for the dialog's card field (created once the key
  // is known). loadStripe caches the SDK internally.
  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey],
  );
  // Imperative handle to the card field so handleSave can tokenise on submit.
  const cardSectionRef = useRef<CardSectionHandle | null>(null);

  // ---- Program-data options (crop types, suppliers, chemicals, …) ----
  const [programOptions, setProgramOptions] = useState<ProgramOptions>(EMPTY_PROGRAM_OPTIONS);
  const [programOptionsLoading, setProgramOptionsLoading] = useState(false);
  // (Fetched per form.country_code — effect lives below the form state.)

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dialogTab, setDialogTab] = useState(0);
  const [auditInfo, setAuditInfo] = useState<{
    created_at: string | null; updated_at: string | null;
    created_by: string | null; updated_by: string | null;
  } | null>(null);
  // Live plan name from Stripe (loaded with the edit record); null when unknown.
  const [stripePlanName, setStripePlanName] = useState<string | null>(null);
  // Reset Password tab — company admin email (loaded with the record) + form fields.
  const [adminEmail, setAdminEmail] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPw, setResettingPw] = useState(false);
  const [requiringReset, setRequiringReset] = useState(false);
  // Create-admin form (shown on the Reset Password tab when no admin exists yet).
  const [createAdminEmail, setCreateAdminEmail] = useState('');
  const [createAdminFirst, setCreateAdminFirst] = useState('');
  const [createAdminLast, setCreateAdminLast] = useState('');
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);

  // Program options are split by catalogue country (crop types / water rates
  // are shared). Refetch whenever the form's country changes; AU is the default.
  const programCountry = form.country_code;
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // Drop the other country's catalogue items straight away so they can't be
    // picked while the new list loads.
    setProgramOptions((prev) => ({ ...prev, suppliers: [], chemicals: [], fertilizers: [], soilTests: [] }));
    setProgramOptionsLoading(true);
    void fetch(`${getApiUrl()}/api/v1/admin/companies/program-options?country=${programCountry}`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((body: ProgramOptions) => { if (!cancelled) setProgramOptions({ ...EMPTY_PROGRAM_OPTIONS, ...body }); })
      .catch(() => { /* non-critical — pickers show "No items available" */ })
      .finally(() => { if (!cancelled) setProgramOptionsLoading(false); });
    return () => { cancelled = true; };
  }, [token, programCountry]);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ---- State / Suburb cascading dropdowns ----
  const [stateOptions, setStateOptions]   = useState<readonly StateOption[]>([]);
  const [suburbOptions, setSuburbOptions] = useState<readonly SuburbOption[]>([]);
  const [loadingSuburbs, setLoadingSuburbs] = useState(false);
  const suburbDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId]       = useState<string | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting]       = useState(false);

  // ---- Restore dialog ----
  const [restoreId, setRestoreId]       = useState<string | null>(null);
  const [restoreLabel, setRestoreLabel] = useState('');
  const [restoring, setRestoring]       = useState(false);

  // ---- Permanent-delete (purge) dialog ----
  const [purgeId, setPurgeId]       = useState<string | null>(null);
  const [purgeLabel, setPurgeLabel] = useState('');
  const [purging, setPurging]       = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{
    open: boolean; message: string; severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/companies`);
      url.searchParams.set('page',  String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);
      if (showDeleted) url.searchParams.set('deleted', 'true');
      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: CompanyRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch, showDeleted]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // Reset page when switching tabs
  useEffect(() => { setPage(0); setSearch(''); }, [listTab]);

  // Load state options when dialog opens
  useEffect(() => {
    if (!dialogOpen || !token) return;
    void (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/v1/admin/postcodes/states`, {
          headers: authHeaders(token),
        });
        if (res.ok) {
          const json = (await res.json()) as { data: StateOption[] };
          setStateOptions(json.data);
        }
      } catch { /* silent */ }
    })();
  }, [dialogOpen, token]);

  // Fetch suburbs for the currently-selected state. With no `search` arg the
  // API returns the first 50 alphabetically, so the dropdown shows options
  // the moment the user opens it after picking a state.
  const fetchSuburbsForState = useCallback(
    async (stateId: string, search: string): Promise<readonly SuburbOption[]> => {
      if (!stateId || !token) return [];
      const url = new URL(`${getApiUrl()}/api/v1/admin/postcodes/suburbs-by-state`);
      url.searchParams.set('state_id', stateId);
      if (search.trim().length >= 1) url.searchParams.set('search', search.trim());
      try {
        const res = await fetch(url.toString(), { headers: authHeaders(token) });
        if (!res.ok) return [];
        const json = (await res.json()) as { data: SuburbOption[] };
        return json.data;
      } catch {
        return [];
      }
    },
    [token],
  );

  // Pre-populate suburb dropdown whenever the state changes, so the user can
  // open the list and browse without having to type first.
  useEffect(() => {
    if (!dialogOpen) return;
    if (!form.state) { setSuburbOptions([]); return; }
    let cancelled = false;
    setLoadingSuburbs(true);
    void fetchSuburbsForState(form.state, '').then((rows) => {
      if (cancelled) return;
      setSuburbOptions((prev) => {
        // Preserve the currently-selected suburb in the list if the API
        // didn't return it (rare, but possible if the list was set during
        // openEdit before this fetch ran).
        const selected = form.suburb
          ? prev.find((o) => String(o.id) === form.suburb)
          : null;
        if (selected && !rows.some((r) => String(r.id) === form.suburb)) {
          return [selected, ...rows];
        }
        return rows;
      });
      setLoadingSuburbs(false);
    });
    return () => { cancelled = true; };
    // We deliberately exclude form.suburb to avoid re-fetching when the user
    // picks a suburb. The fetch should fire only on state change / dialog open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.state, dialogOpen, fetchSuburbsForState]);

  const handleSuburbInputChange = useCallback((_e: React.SyntheticEvent, inputValue: string) => {
    if (suburbDebounceRef.current) clearTimeout(suburbDebounceRef.current);
    if (!form.state) { setSuburbOptions([]); return; }
    // Empty input → reset to the initial alphabetical list. Typed input →
    // debounce and search.
    if (inputValue.length === 0) {
      setLoadingSuburbs(true);
      void fetchSuburbsForState(form.state, '').then((rows) => {
        setSuburbOptions(rows);
        setLoadingSuburbs(false);
      });
      return;
    }
    setLoadingSuburbs(true);
    suburbDebounceRef.current = setTimeout(() => {
      void fetchSuburbsForState(form.state, inputValue).then((rows) => {
        setSuburbOptions(rows);
        setLoadingSuburbs(false);
      });
    }, 250);
  }, [form.state, fetchSuburbsForState]);

  // ---------------------------------------------------------------------------
  // Open Add / Edit / close dialog
  // ---------------------------------------------------------------------------
  const closeDialog = () => { setDialogOpen(false); setDialogTab(0); };

  const openAdd = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setSuburbOptions([]);
    setFormError(null);
    setAuditInfo(null);
    setStripePlanName(null);
    setAdminEmail(null);
    setNewPassword('');
    setConfirmPassword('');
    setCreateAdminEmail('');
    setCreateAdminFirst('');
    setCreateAdminLast('');
    setDialogTab(0);
    setDialogOpen(true);
  };

  const openEdit = async (id: string) => {
    if (!token) return;
    setFormError(null);
    setDialogTab(0);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/companies/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: CompanyRow };
      const r = json.data;
      setForm({
        company_name:          r.company_name          ?? '',
        email_address:         r.email_address         ?? '',
        phone_no:              r.phone_no              ?? '',
        mobile_number:         r.mobile_number         ?? '',
        street:                r.street                ?? '',
        suburb:                r.suburb                ?? '',
        postcode:              r.postcode              ?? '',
        state:                 r.state                 ?? '',
        country:               r.country               ?? '',
        country_code:          r.country_code ?? 'AU',
        currency_code:         r.currency_code ?? '',
        tax_label:             r.tax_label ?? '',
        tax_rate_pct:          r.tax_rate !== null ? String(Math.round(Number(r.tax_rate) * 10000) / 100) : '',
        // Absent only from an API older than the column: it defaulted to on.
        payroll_enabled:       r.payroll_enabled ?? true,
        lat:                   r.lat != null ? String(r.lat) : '',
        lon:                   r.lon != null ? String(r.lon) : '',
        abn:                   r.abn                   ?? '',
        first_name:            r.first_name            ?? '',
        last_name:             r.last_name             ?? '',
        title:                 r.title                 ?? '',
        status:                r.status                ?? '',
        paid_sub:              r.paid_sub ?? false,
        customer_id:           r.customer_id           ?? '',
        subscription_id:       r.subscription_id       ?? '',
        logo_url:              r.logo_url              ?? '',
        sales_tax_default:     r.sales_tax_default     ?? '',
        setup_wizard:          r.setup_wizard != null ? String(r.setup_wizard) : '0',
        plan_type:             r.plan_type             ?? 0,
        agronomist:            r.agronomist ?? 0,
        block_broadacre:       r.block_broadacre ?? false,
        block_fixed_planting:  r.block_fixed_planting ?? false,
        block_glasshouse:      r.block_glasshouse ?? false,
        block_pivot:           r.block_pivot ?? false,
        block_fixed_irrigation: r.block_fixed_irrigation ?? false,
        onboarding_mode:       (r.onboarding_mode as 'pay_first' | 'free_trial' | 'internal' | null) ?? 'pay_first',
        trial_days:            14,
        subscription_state:    r.subscription_state ?? '',
        trial_ends_at:         r.trial_ends_at ?? '',
        // Program-data pickers are create-mode only; leave empty on edit.
        crop_type_ids:         [],
        supplier_ids:          [],
        chemical_ids:          [],
        fertilizer_ids:        [],
        soil_test_lab_ids:     [],
        water_rate_ids:        [],
      });
      // GB rows hold the town as text, not a postcode_australia_list id.
      if (r.country_code !== 'GB' && r.suburb && r.suburb_name) {
        setSuburbOptions([{
          id: Number(r.suburb),
          suburb: r.suburb_name,
          postcode: r.postcode ?? '',
          country: r.country ?? '',
          lat: r.lat,
          lon: r.lon,
        }]);
      } else {
        setSuburbOptions([]);
      }
      setAuditInfo({
        created_at: r.created_at ?? null,
        updated_at: r.updated_at ?? null,
        created_by: r.created_by ?? null,
        updated_by: r.updated_by ?? null,
      });
      setStripePlanName(r.stripe_plan_name ?? null);
      setAdminEmail(r.admin_email ?? null);
      setNewPassword('');
      setConfirmPassword('');
      // Prefill the create-admin form from the company contact (used only if no admin exists).
      setCreateAdminEmail(r.email_address ?? '');
      setCreateAdminFirst(r.first_name ?? '');
      setCreateAdminLast(r.last_name ?? '');
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const trimmedName = form.company_name.trim();
    if (!trimmedName)            { setFormError('Company name is required.');                   return; }
    if (trimmedName.length > 350) { setFormError('Company name must be 350 characters or fewer.'); return; }
    const taxPct = Number(form.tax_rate_pct);
    if (form.tax_rate_pct.trim() === '' || !(taxPct >= 0 && taxPct < 100)) { setFormError('Tax rate must be a percentage from 0 to under 100.'); return; }
    if (!form.tax_label.trim()) { setFormError('Tax name is required (e.g. GST, VAT).'); return; }

    // Creating a new company goes through the onboarding flow which requires
    // an admin user (first/last/email). Editing keeps the existing PUT shape.
    if (!editingId) {
      if (!form.first_name.trim() || !form.last_name.trim()) {
        setFormError('Admin user first and last name are required.');
        return;
      }
      if (!form.email_address.trim()) {
        setFormError('Admin user email is required.');
        return;
      }
      if (form.onboarding_mode !== 'internal' && (form.plan_type < 1 || form.plan_type > 3)) {
        setFormError('Pick a plan (Essential / Standard / Premium) for paid onboarding.');
        return;
      }
    }

    setSaving(true);
    setFormError(null);
    try {
      let url: string;
      let method: 'POST' | 'PUT';
      let body: Record<string, unknown>;

      // Pay-first: tokenise the superadmin-entered card via Stripe Elements
      // BEFORE hitting our API. Only the resulting pm_... id is sent — raw card
      // data never touches our servers. Abort (and jump to the Subscription tab)
      // if the card is missing/invalid so nothing is created.
      let paymentMethodId: string | undefined;
      if (!editingId && form.onboarding_mode === 'pay_first') {
        if (!cardSectionRef.current) {
          setDialogTab(SUMMARY_TAB);
          setFormError('Card entry is not ready yet. Please wait a moment and try again.');
          setSaving(false);
          return;
        }
        const pm = await cardSectionRef.current.createPaymentMethod();
        if (!pm.ok) {
          setDialogTab(SUMMARY_TAB);
          setFormError(pm.error);
          setSaving(false);
          return;
        }
        paymentMethodId = pm.paymentMethodId;
      }

      if (editingId) {
        // Edit mode keeps the legacy PUT /:id shape unchanged. Stripe IDs and
        // subscription_state are read-only in the UI but still passed through
        // so the existing handler is happy.
        url = `${getApiUrl()}/api/v1/admin/companies/${editingId}`;
        method = 'PUT';
        body = {
          company_name:          trimmedName,
          email_address:         form.email_address         || null,
          phone_no:              form.phone_no              || null,
          mobile_number:         form.mobile_number         || null,
          street:                form.street                || null,
          suburb:                form.suburb                || null,
          postcode:              form.postcode              || null,
          state:                 form.state                 || null,
          country:               form.country               || null,
          country_code:          form.country_code,
          currency_code:         form.currency_code,
          tax_label:             form.tax_label.trim(),
          tax_rate:              Number(form.tax_rate_pct) / 100,
          payroll_enabled:       form.payroll_enabled,
          lat:                   form.lat   !== '' ? parseFloat(form.lat)   : null,
          lon:                   form.lon   !== '' ? parseFloat(form.lon)   : null,
          abn:                   form.abn                   || null,
          first_name:            form.first_name            || null,
          last_name:             form.last_name             || null,
          title:                 form.title                 || null,
          status:                form.status                || null,
          paid_sub:              form.paid_sub,
          customer_id:           form.customer_id           || null,
          subscription_id:       form.subscription_id       || null,
          logo_url:              form.logo_url              || null,
          sales_tax_default:     form.sales_tax_default     || null,
          setup_wizard:          form.setup_wizard !== '' ? parseInt(form.setup_wizard, 10) : 0,
          plan_type:             form.plan_type,
          agronomist:            form.agronomist,
          block_broadacre:       form.block_broadacre,
          block_fixed_planting:  form.block_fixed_planting,
          block_glasshouse:      form.block_glasshouse,
          block_pivot:           form.block_pivot,
          block_fixed_irrigation: form.block_fixed_irrigation,
        };
      } else {
        // Create mode uses the new /onboard endpoint which creates the
        // company, the first companies_admin auth user, and (for paid modes)
        // the Stripe customer + checkout/trial sub.
        url = `${getApiUrl()}/api/v1/admin/companies/onboard`;
        method = 'POST';
        body = {
          onboarding_mode:       form.onboarding_mode,
          plan_type:             form.onboarding_mode === 'internal' ? undefined : form.plan_type,
          trial_days:            form.onboarding_mode === 'free_trial' ? form.trial_days : undefined,
          payment_method_id:     paymentMethodId,
          admin_user: {
            email:         form.email_address.trim(),
            first_name:    form.first_name.trim(),
            last_name:     form.last_name.trim(),
            mobile_number: form.mobile_number.trim() || undefined,
          },
          company_name:          trimmedName,
          email_address:         form.email_address         || null,
          phone_no:              form.phone_no              || null,
          mobile_number:         form.mobile_number         || null,
          street:                form.street                || null,
          suburb:                form.suburb                || null,
          postcode:              form.postcode              || null,
          state:                 form.state                 || null,
          country:               form.country               || null,
          country_code:          form.country_code,
          currency_code:         form.currency_code,
          tax_label:             form.tax_label.trim(),
          tax_rate:              Number(form.tax_rate_pct) / 100,
          payroll_enabled:       form.payroll_enabled,
          lat:                   form.lat !== '' ? parseFloat(form.lat) : null,
          lon:                   form.lon !== '' ? parseFloat(form.lon) : null,
          abn:                   form.abn                   || null,
          title:                 form.title                 || null,
          logo_url:              form.logo_url              || null,
          sales_tax_default:     form.sales_tax_default     || null,
          agronomist:            form.agronomist,
          block_broadacre:       form.block_broadacre,
          block_fixed_planting:  form.block_fixed_planting,
          block_glasshouse:      form.block_glasshouse,
          block_pivot:           form.block_pivot,
          block_fixed_irrigation: form.block_fixed_irrigation,
          // Program-data selections — omit empty arrays so the payload stays lean.
          crop_type_ids:         form.crop_type_ids.length     ? form.crop_type_ids     : undefined,
          supplier_ids:          form.supplier_ids.length      ? form.supplier_ids      : undefined,
          chemical_ids:          form.chemical_ids.length      ? form.chemical_ids      : undefined,
          fertilizer_ids:        form.fertilizer_ids.length    ? form.fertilizer_ids    : undefined,
          soil_test_lab_ids:     form.soil_test_lab_ids.length ? form.soil_test_lab_ids : undefined,
          water_rate_ids:        form.water_rate_ids.length    ? form.water_rate_ids    : undefined,
        };
      }

      const res = await fetch(url, {
        method,
        headers: authHeaders(token),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const successMessage = editingId
        ? 'Company updated.'
        : form.onboarding_mode === 'pay_first' ? 'Company created — payment link sent.'
          : form.onboarding_mode === 'free_trial' ? 'Trial started — magic link sent.'
            : 'Internal company created — magic link sent.';
      closeDialog();
      showSnack(successMessage);
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Reset Password — set a new Supabase Auth password for the company admin
  // ---------------------------------------------------------------------------
  const handleResetPassword = async () => {
    if (!editingId || !token) return;
    if (newPassword.length < 8) { setFormError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setFormError('Passwords do not match.'); return; }

    setResettingPw(true);
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/companies/${editingId}/reset-admin-password`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ password: newPassword }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { email?: string | null };
      setNewPassword('');
      setConfirmPassword('');
      showSnack(`Password reset for ${data.email ?? adminEmail ?? 'company admin'}.`);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setResettingPw(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Require password change on next login (sets force_password_reset flag)
  // ---------------------------------------------------------------------------
  const handleRequirePasswordReset = async () => {
    if (!editingId || !token) return;
    setRequiringReset(true);
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/companies/${editingId}/require-password-reset`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      showSnack(`${adminEmail ?? 'The admin'} must set a new password on next login.`);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setRequiringReset(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Create Admin — create the company's admin auth user when none exists
  // ---------------------------------------------------------------------------
  const handleCreateAdmin = async () => {
    if (!editingId || !token) return;
    if (!createAdminEmail.trim()) { setFormError('Admin email is required.'); return; }
    if (newPassword.length < 8) { setFormError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setFormError('Passwords do not match.'); return; }

    setCreatingAdmin(true);
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/companies/${editingId}/create-admin`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          email: createAdminEmail.trim(),
          first_name: createAdminFirst.trim(),
          last_name: createAdminLast.trim(),
          password: newPassword,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { email?: string; adopted?: boolean };
      // Flip the tab into reset mode now that an admin exists.
      setAdminEmail(data.email ?? createAdminEmail.trim());
      setNewPassword('');
      setConfirmPassword('');
      const msg = data.adopted
        ? `Existing user adopted as admin for ${data.email ?? createAdminEmail.trim()}. Password updated.`
        : `Admin user created for ${data.email ?? createAdminEmail.trim()}.`;
      showSnack(msg);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Create admin failed');
    } finally {
      setCreatingAdmin(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete (soft)
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: string, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/companies/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Company deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Restore
  // ---------------------------------------------------------------------------
  const confirmRestore = (id: string, label: string) => { setRestoreId(id); setRestoreLabel(label); };

  const handleRestore = async () => {
    if (!restoreId || !token) return;
    setRestoring(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/companies/${restoreId}/restore`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRestoreId(null);
      showSnack('Company restored.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Restore failed', 'error');
    } finally {
      setRestoring(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Permanent delete (purge) — irreversible removal of all company data
  // ---------------------------------------------------------------------------
  const confirmPurge = (id: string, label: string) => { setPurgeId(id); setPurgeLabel(label); };

  const handlePurge = async () => {
    if (!purgeId || !token) return;
    setPurging(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/companies/${purgeId}/purge`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; warnings?: string[] };
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      setPurgeId(null);
      showSnack(
        data.warnings && data.warnings.length > 0
          ? `Company permanently deleted (with warnings: ${data.warnings.join('; ')}).`
          : 'Company permanently deleted.',
      );
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Permanent delete failed', 'error');
    } finally {
      setPurging(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------
  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <IconButton onClick={() => navigate('/dashboard')} size="small">
          <BackIcon />
        </IconButton>
        <BusinessIcon color="primary" />
        <Typography variant="h5" fontWeight={600} flexGrow={1}>
          Companies
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Add Company
        </Button>
      </Box>

      {/* List tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={listTab} onChange={(_, v: 0 | 1) => setListTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Active" value={0} />
          <Tab label="Deleted" value={1} />
        </Tabs>

        {/* Search + rows-per-page */}
        <Box display="flex" alignItems="center" gap={2} p={2}>
          <TextField
            size="small"
            placeholder="Search companies…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          {activeCompaniesThisMonth !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: (theme) =>
                  `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.dark, 0.08)} 100%)`,
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.success.main, 0.35),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.22),
                  color: 'success.light',
                  flexShrink: 0,
                }}
              >
                <PeopleAltIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1} color="success.light">
                  {activeCompaniesThisMonth}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                  Active Companies This Month
                </Typography>
              </Box>
            </Box>
          )}
          {activeCompaniesThisWeek !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: (theme) =>
                  `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.dark, 0.08)} 100%)`,
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.info.main, 0.35),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.22),
                  color: 'info.light',
                  flexShrink: 0,
                }}
              >
                <CalendarMonthIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1} color="info.light">
                  {activeCompaniesThisWeek}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                  Active Companies This Week
                </Typography>
              </Box>
            </Box>
          )}
          {avgActiveCompaniesYear !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderRadius: 2,
                minWidth: 260,
                whiteSpace: 'nowrap',
                background: (theme) =>
                  `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)} 0%, ${alpha(theme.palette.warning.dark, 0.08)} 100%)`,
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.warning.main, 0.35),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.22),
                  color: 'warning.light',
                  flexShrink: 0,
                }}
              >
                <InsightsIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1} color="warning.light">
                  {avgActiveCompaniesYear}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.2} sx={{ whiteSpace: 'nowrap' }}>
                  Avg Active Companies / Month · {new Date().getFullYear()}
                </Typography>
              </Box>
            </Box>
          )}
          <Box flexGrow={1} />
        </Box>
      </Paper>

      {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <AppTableHead>
              <TableRow>
                <AppTableHeadCell>Company</AppTableHeadCell>
                <AppTableHeadCell>Email</AppTableHeadCell>
                <AppTableHeadCell>Contact</AppTableHeadCell>
                <AppTableHeadCell>Mobile</AppTableHeadCell>
                <AppTableHeadCell>Suburb</AppTableHeadCell>
                <AppTableHeadCell>Plan</AppTableHeadCell>
                <AppTableHeadCell>Status</AppTableHeadCell>
                <AppTableHeadCell>Last Payment</AppTableHeadCell>
                {showDeleted && <AppTableHeadCell>Deleted</AppTableHeadCell>}
                <AppTableHeadCell align="right">Actions</AppTableHeadCell>
              </TableRow>
            </AppTableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={showDeleted ? 10 : 9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showDeleted ? 10 : 9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No companies found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <AppTableRow key={row.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.company_name}</Typography>
                      {row.abn && (
                        <Typography variant="caption" color="text.secondary">ABN: {row.abn}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.email_address ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{contactName(row) || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.mobile_number ?? row.phone_no ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.suburb_name ?? '—'}{row.country_code === 'GB' ? ' (UK)' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={PLAN_LABELS[row.plan_type] ?? String(row.plan_type)}
                        color={PLAN_COLORS[row.plan_type] ?? 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {row.deleted_at ? (
                        <Chip
                          label="Deleted"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      ) : row.stripe_status ? (
                        <Chip
                          label={row.stripe_status.replace(/_/g, ' ')}
                          color={STRIPE_STATUS_COLORS[row.stripe_status] ?? 'default'}
                          size="small"
                          variant="filled"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      ) : row.status ? (
                        <Chip
                          label={row.status}
                          color={row.status === 'paid' ? 'success' : 'default'}
                          size="small"
                          variant="filled"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="success.main">
                        {row.stripe_last_payment ? fmtAuDate(row.stripe_last_payment) : '—'}
                      </Typography>
                    </TableCell>
                    {showDeleted && (
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {fmtAuDate(row.deleted_at)}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      {showDeleted ? (
                        <>
                          <Tooltip title="Restore">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => confirmRestore(row.id, row.company_name)}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Permanently delete all data">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => confirmPurge(row.id, row.company_name)}
                            >
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => void openEdit(row.id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => confirmDelete(row.id, row.company_name)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </AppTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme.palette.mode === 'light'
            ? alpha(theme.palette.primary.main, 0.09)
            : darken(theme.palette.background.paper, 0.35),
          px: 2,
          py: 1.5,
        })}>
          <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}>
            {loading ? 'Loading…' : total === 0 ? 'No records found'
              : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>Show</Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                  '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                  '& .MuiSelect-select': { py: 0.5, px: 1 },
                })}
              >
                {ROWS_PER_PAGE_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </Box>
            {totalPages > 0 && (
              <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                Page {page + 1} of {totalPages}
              </Typography>
            )}
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, p) => setPage(p - 1)}
                size="small"
                disabled={loading}
                sx={(theme) => ({
                  '& .MuiPaginationItem-root': {
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  },
                  '& .Mui-selected': {
                    backgroundColor: `${theme.palette.primary.main} !important`,
                    color: theme.palette.primary.contrastText,
                  },
                  '& .Mui-disabled': { opacity: 0.5 },
                })}
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* -----------------------------------------------------------------------
          Add / Edit Dialog
      ----------------------------------------------------------------------- */}
      {/* backgroundImage:'none' flattens the dark-mode elevation overlay so the
          dialog surface equals the raw paper colour — this makes the label chip
          (below) the exact same colour as the surface, so it reads as a clean
          notch (no visible box), matching the flat outlined panels in blocks. */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { backgroundImage: 'none' } } }}
      >
        <DialogTitle>
          {editingId ? 'Edit Company' : 'Add Company'}
        </DialogTitle>

        {/* Dialog tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={dialogTab} onChange={(_, v: number) => setDialogTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Details" value={0} />
            <Tab label="Subscription" value={1} />
            <Tab label="Block Visibility" value={2} />
            {/* Program-data pickers — create mode only (persisted via /onboard) */}
            {!editingId && PROGRAM_CATEGORIES.map((cat, i) => (
              <Tab key={cat.formKey} label={cat.label} value={3 + i} />
            ))}
            {!editingId && <Tab label="Summary" value={SUMMARY_TAB} />}
            {editingId && <Tab label="Reset Password" value={3} />}
            {editingId && <Tab label="Agronomists" value={4} />}
          </Tabs>
        </Box>

        {/* Outlined-label fix (applies to EVERY field in this dialog):
            MUI cuts the notch in the field border by measuring the label width
            when the dialog opens. With the custom Inter font + the dialog open
            animation that measurement can come out wrong, leaving the top border
            running through the label text ("Company Name" strike-through).
            Instead of relying on the notch, we sit the shrunk label on an opaque
            paper-coloured chip — visually identical to a clean notch (the gap is
            paper colour anyway) but immune to the measurement timing. */}
        <DialogContent sx={{
          display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important',
          '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            bgcolor: 'background.paper',
            px: 0.5,
            borderRadius: 0.5,
          },
        }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          {/* ---- Tab 0: Details ---- */}
          {dialogTab === 0 && (
            <Stack spacing={2} mt={1}>
              <TextField
                label="Company Name *"
                value={form.company_name}
                onChange={(e) => setField('company_name', e.target.value)}
                fullWidth
                size="small"
                inputProps={{ maxLength: 350 }}
              />
              <TextField
                label="Title / Tagline"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                fullWidth
                size="small"
                inputProps={{ maxLength: 300 }}
              />
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  {editingId ? 'Contact' : 'Contact / first admin user'}
                </Typography>
              </Divider>
              {!editingId && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 0.5, display: 'block' }}>
                  These details create the first <strong>companies_admin</strong> account for this company.
                  The admin will receive an onboarding email at this address.
                </Typography>
              )}
              <Box sx={{ width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={editingId ? 'First Name' : 'First Name *'}
                    value={form.first_name}
                    onChange={(e) => setField('first_name', e.target.value)}
                    fullWidth size="small" inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={editingId ? 'Last Name' : 'Last Name *'}
                    value={form.last_name}
                    onChange={(e) => setField('last_name', e.target.value)}
                    fullWidth size="small" inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={editingId ? 'Email Address' : 'Email Address *'}
                    value={form.email_address}
                    onChange={(e) => setField('email_address', e.target.value)}
                    fullWidth size="small" type="email" inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Mobile"
                    value={form.mobile_number}
                    onChange={(e) => setField('mobile_number', e.target.value)}
                    fullWidth size="small" inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Phone"
                    value={form.phone_no}
                    onChange={(e) => setField('phone_no', e.target.value)}
                    fullWidth size="small" inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={form.country_code === 'GB' ? 'Company / VAT number' : 'ABN'}
                    value={form.abn}
                    onChange={(e) => setField('abn', e.target.value)}
                    fullWidth size="small" inputProps={{ maxLength: 100 }}
                  />
                </Grid>
              </Grid>
              </Box>

              <Divider><Typography variant="caption" color="text.secondary">Address</Typography></Divider>
              <FormControl size="small" sx={{ width: '33%' }}>
                <InputLabel>Country</InputLabel>
                <Select
                  label="Country"
                  value={form.country_code}
                  onChange={(e) => {
                    const code = e.target.value;
                    const c = findCountry(countries, code);
                    if (!c) return;
                    setForm((prev) => ({
                      ...prev,
                      country_code: code,
                      country: c.name.toUpperCase(),
                      // Currency + tax reset to the new country's defaults (editable below).
                      currency_code: c.currency_code,
                      tax_label: c.tax_label,
                      tax_rate_pct: String(Math.round(c.tax_rate * 10000) / 100),
                      // Payroll is built on the Australian award + Xero/MYOB Payroll AU:
                      // on for Australia, off elsewhere (the switch below can override).
                      payroll_enabled: code === 'AU',
                      street: '', suburb: '', state: '', postcode: '', lat: '', lon: '',
                      // Stripe plan prices exist in AUD only — other currencies are Free trial / Internal.
                      onboarding_mode: c.currency_code !== 'AUD' && prev.onboarding_mode === 'pay_first' ? 'free_trial' : prev.onboarding_mode,
                      // Country-specific catalogue picks can't carry across countries
                      // (crop types / water rates are shared and kept).
                      ...(code !== prev.country_code && !editingId
                        ? { supplier_ids: [], chemical_ids: [], fertilizer_ids: [], soil_test_lab_ids: [] }
                        : {}),
                    }));
                    setSuburbOptions([]);
                  }}
                >
                  {/* From public.countries — a new country appears once its row is added. */}
                  {countries.filter((c) => c.active || c.code === form.country_code).map((c) => (
                    <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                  ))}
                  {countries.length === 0 ? <MenuItem value={form.country_code}>{form.country_code}</MenuItem> : null}
                </Select>
              </FormControl>
              {/* Company currency + sales tax: defaults from the country, editable
                  (e.g. a UK farm that isn't VAT-registered → 0%). */}
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Currency"
                  value={form.currency_code}
                  size="small"
                  sx={{ width: '33%' }}
                  InputProps={{ readOnly: true }}
                  helperText={`${findCountry(countries, form.country_code)?.currency_symbol ?? ''} · set by Country`}
                />
                <TextField
                  label="Tax name"
                  value={form.tax_label}
                  onChange={(e) => setField('tax_label', e.target.value)}
                  size="small"
                  sx={{ width: '33%' }}
                  inputProps={{ maxLength: 20 }}
                  helperText="e.g. GST, VAT"
                />
                <TextField
                  label="Tax rate (%)"
                  value={form.tax_rate_pct}
                  onChange={(e) => setField('tax_rate_pct', e.target.value)}
                  size="small"
                  sx={{ width: '33%' }}
                  type="number"
                  inputProps={{ min: 0, max: 99.99, step: 0.01 }}
                  error={!(Number(form.tax_rate_pct) >= 0 && Number(form.tax_rate_pct) < 100) || form.tax_rate_pct === ''}
                  helperText={(() => {
                    const c = findCountry(countries, form.country_code);
                    return c ? `${c.name} default: ${c.tax_label} ${formatTaxPercent(c.tax_rate)}` : ' ';
                  })()}
                />
              </Stack>
              {/* Payroll switch (companies.payroll_enabled). Off = Timesheets only:
                  no Payroll, Add Leave, Xero/MYOB or staff pay settings in the app. */}
              <Box>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={form.payroll_enabled}
                      onChange={(e) => setField('payroll_enabled', e.target.checked)}
                    />
                  )}
                  label={form.payroll_enabled ? 'Payroll & Xero / MYOB: on' : 'Payroll & Xero / MYOB: off — Timesheets only'}
                />
                <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 6 }}>
                  {form.country_code === 'AU'
                    ? 'Pay is worked out under the Australian Horticulture Award and sent to Xero or MYOB Payroll (Australia).'
                    : 'Payroll is built for Australia (Horticulture Award, Xero / MYOB Payroll AU), so it is off for this country. Staff still record hours on Timesheets.'}
                </Typography>
              </Box>
              {form.country_code === 'GB' ? (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', textAlign: 'center' }}>
                  Type the <strong>Postcode</strong>. Town, county and location fill in automatically — check the town and correct it if needed.
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', textAlign: 'center' }}>
                  Select a <strong>State</strong> first. The <strong>Suburb</strong> dropdown will populate automatically — click to browse, or type to search. Postcode auto-fills when a suburb is selected.
                </Typography>
              )}
              <TextField
                label="Street"
                value={form.street}
                onChange={(e) => setField('street', e.target.value)}
                fullWidth size="small" inputProps={{ maxLength: 200 }}
              />
              {form.country_code === 'GB' ? (
                <Box sx={{ width: '100%' }}>
                  <UkAddressFields
                    apiUrl={getApiUrl()}
                    token={token}
                    value={{ postcode: form.postcode, town: form.suburb, county: form.state, lat: form.lat, lon: form.lon }}
                    onChange={(patch: Partial<UkAddressValue>) => setForm((prev) => ({
                      ...prev,
                      ...(patch.postcode !== undefined ? { postcode: patch.postcode } : {}),
                      ...(patch.town !== undefined ? { suburb: patch.town } : {}),
                      ...(patch.county !== undefined ? { state: patch.county } : {}),
                      ...(patch.lat !== undefined ? { lat: patch.lat } : {}),
                      ...(patch.lon !== undefined ? { lon: patch.lon } : {}),
                    }))}
                  />
                </Box>
              ) : (
                <Box sx={{ width: '100%' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={4}>
                    {/* State selector */}
                    <FormControl fullWidth size="small">
                      <InputLabel>State</InputLabel>
                      <Select
                        label="State"
                        value={form.state}
                        onChange={(e) => {
                          setField('state', e.target.value);
                          setField('suburb', '');
                          setField('postcode', '');
                          setSuburbOptions([]);
                        }}
                      >
                        <MenuItem value=""><em>— None —</em></MenuItem>
                        {stateOptions.map((s) => (
                          <MenuItem key={s.state_id} value={String(s.state_id)}>{s.state}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    {/* Suburb autocomplete */}
                    <Autocomplete
                      size="small"
                      options={suburbOptions}
                      loading={loadingSuburbs}
                      getOptionLabel={(o) => (typeof o === 'string' ? o : o.suburb)}
                      filterOptions={(x) => x}
                      openOnFocus
                      value={
                        form.suburb
                          ? (suburbOptions.find((o) => String(o.id) === form.suburb) ?? null)
                          : null
                      }
                      onChange={(_, v) => {
                        if (v && typeof v !== 'string') {
                          setField('suburb', String(v.id));
                          setField('postcode', v.postcode);
                          setField('country', v.country || 'AUSTRALIA');
                          setField('lat', v.lat != null ? String(v.lat) : '');
                          setField('lon', v.lon != null ? String(v.lon) : '');
                        } else {
                          setField('suburb', '');
                          setField('postcode', '');
                          setField('lat', '');
                          setField('lon', '');
                        }
                      }}
                      onInputChange={handleSuburbInputChange}
                      disabled={!form.state}
                      renderInput={(params) => (
                        // @ts-expect-error: MUI Autocomplete renderInput params have optional props incompatible with exactOptionalPropertyTypes
                        <TextField
                          {...params}
                          size="small"
                          label="Suburb"
                          helperText={!form.state ? 'Select a state first' : 'Click to browse, or type to search'}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingSuburbs ? <CircularProgress size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Postcode"
                      value={form.postcode}
                      onChange={(e) => setField('postcode', e.target.value)}
                      fullWidth size="small" inputProps={{ maxLength: 20 }}
                    />
                  </Grid>
                </Grid>
                </Box>
              )}
              <Box sx={{ width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Billing country"
                    value={form.country}
                    fullWidth size="small"
                    InputProps={{ readOnly: true }}
                    helperText="Set by Country above"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Latitude"
                    value={form.lat}
                    fullWidth size="small" type="number"
                    InputProps={{ readOnly: true }}
                    helperText={form.country_code === 'GB' ? 'Auto-filled from postcode' : 'Auto-filled from suburb'}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Longitude"
                    value={form.lon}
                    fullWidth size="small" type="number"
                    InputProps={{ readOnly: true }}
                    helperText={form.country_code === 'GB' ? 'Auto-filled from postcode' : 'Auto-filled from suburb'}
                  />
                </Grid>
              </Grid>
              </Box>
              {form.country_code === 'GB' && form.lat !== '' && form.lon !== '' ? (
                <UkWeatherPreview apiUrl={getApiUrl()} token={token} lat={form.lat} lon={form.lon} name={form.suburb} />
              ) : null}
            </Stack>
          )}

          {/* ---- Tab 1: Subscription ---- */}
          {dialogTab === 1 && (
            <Stack spacing={2} mt={1}>
              {!editingId ? (
                // CREATE mode: pick onboarding mode + plan + (for trial) duration.
                // Stripe customer + subscription are created server-side.
                <>
                  <FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                      Onboarding mode
                    </Typography>
                    <RadioGroup
                      value={form.onboarding_mode}
                      onChange={(e) => setField('onboarding_mode', e.target.value as FormState['onboarding_mode'])}
                    >
                      <FormControlLabel
                        value="pay_first"
                        control={<Radio size="small" />}
                        disabled={form.currency_code !== 'AUD'}
                        label={form.currency_code !== 'AUD'
                          ? `Pay-first — not available in ${form.currency_code} yet (plan prices are AUD only)`
                          : 'Pay-first — admin enters card details, immediate paid access'}
                      />
                      <FormControlLabel
                        value="free_trial"
                        control={<Radio size="small" />}
                        label="Free trial — time-bounded, must convert to paid before expiry"
                      />
                      <FormControlLabel
                        value="internal"
                        control={<Radio size="small" />}
                        label="Internal / staff — no payment, no expiry"
                      />
                    </RadioGroup>
                  </FormControl>

                  {form.onboarding_mode !== 'internal' && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Plan</InputLabel>
                      <Select
                        label="Plan"
                        value={form.plan_type === 0 ? 1 : form.plan_type}
                        onChange={(e) => setField('plan_type', Number(e.target.value))}
                      >
                        {planCatalog.length > 0
                          ? planCatalog.map((p) => (
                            <MenuItem key={p.plan_type} value={p.plan_type}>{planOptionLabel(p)}</MenuItem>
                          ))
                          : [
                            <MenuItem key={1} value={1}>Essential</MenuItem>,
                            <MenuItem key={2} value={2}>Standard</MenuItem>,
                            <MenuItem key={3} value={3}>Premium</MenuItem>,
                          ]}
                      </Select>
                    </FormControl>
                  )}

                  {form.onboarding_mode === 'free_trial' && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Trial duration (days)</InputLabel>
                      <Select
                        label="Trial duration (days)"
                        value={TRIAL_DAY_OPTIONS.includes(form.trial_days) ? form.trial_days : 14}
                        onChange={(e) => setField('trial_days', Number(e.target.value))}
                      >
                        {TRIAL_DAY_OPTIONS.map((d) => (
                          <MenuItem key={d} value={d}>{d} days</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Card entry lives on the final Summary step (co-located with the
                      Create Company button) so the Stripe iframe is still mounted when
                      we tokenise on submit. Entering it here would unmount as soon as
                      the wizard advances, silently breaking pay-first onboarding. */}
                  {form.onboarding_mode === 'pay_first' && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      You&apos;ll enter the card details on the final <strong>Summary</strong> step,
                      right before creating the company.
                    </Alert>
                  )}

                  <Alert severity="info" sx={{ mt: 1 }}>
                    {form.onboarding_mode === 'pay_first' && (
                      <>The card is charged immediately for the first billing period and saved for renewals. The admin user is emailed a magic-link login and gets paid access straight away.</>
                    )}
                    {form.onboarding_mode === 'free_trial' && (
                      <>The admin user will receive a magic-link login email immediately. A trial subscription is created in Stripe; they must add a card before the trial ends.</>
                    )}
                    {form.onboarding_mode === 'internal' && (
                      <>No Stripe records are created. The admin user receives a magic-link login email. Suitable for staff/agronomist accounts.</>
                    )}
                  </Alert>
                </>
              ) : (
                // EDIT mode: subscription state is read-only — driven by Stripe webhooks.
                <>
                  <Box sx={{ p: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.06), borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                      Subscription state (read-only)
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2"><strong>Plan:</strong> {stripePlanName ?? PLAN_LABELS[form.plan_type] ?? `Tier ${form.plan_type}`}</Typography>
                      {form.trial_ends_at && (
                        <Typography variant="body2"><strong>Trial ends:</strong> {fmtAuDate(form.trial_ends_at)}</Typography>
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                      Plan changes are made by the company admin from in-app billing.
                      Cancellation happens via the Delete action.
                    </Typography>
                  </Box>
                </>
              )}

              <Divider><Typography variant="caption" color="text.secondary">Display & defaults</Typography></Divider>

              {/* Agronomist seats — edit mode has its own dedicated tab. */}
              {!editingId && (
                <FormControl fullWidth size="small">
                  <InputLabel>Agronomists allowed</InputLabel>
                  <Select
                    label="Agronomists allowed"
                    value={form.agronomist}
                    onChange={(e) => setField('agronomist', Number(e.target.value))}
                  >
                    {AGRONOMIST_SEAT_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>{n === 0 ? 'None' : n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl fullWidth size="small">
                <InputLabel>Setup Wizard</InputLabel>
                <Select
                  label="Setup Wizard"
                  value={form.setup_wizard}
                  onChange={(e) => setField('setup_wizard', String(e.target.value))}
                >
                  <MenuItem value="0">Complete</MenuItem>
                  <MenuItem value="1">In Progress</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          {/* ---- Tab 2: Block Visibility ---- */}
          {dialogTab === 2 && (
            <Stack spacing={1} mt={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enable the block types available to this company.
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={form.block_broadacre} onChange={(e) => setField('block_broadacre', e.target.checked)} size="small" />}
                  label="Broadacre"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.block_fixed_planting} onChange={(e) => setField('block_fixed_planting', e.target.checked)} size="small" />}
                  label="Fixed Planting"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.block_glasshouse} onChange={(e) => setField('block_glasshouse', e.target.checked)} size="small" />}
                  label="Glasshouse"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.block_pivot} onChange={(e) => setField('block_pivot', e.target.checked)} size="small" />}
                  label="Pivot"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.block_fixed_irrigation} onChange={(e) => setField('block_fixed_irrigation', e.target.checked)} size="small" />}
                  label="Fixed Irrigation"
                />
              </FormGroup>
            </Stack>
          )}

          {/* ---- Tabs 3–8: Program-data pickers (create mode only) ---- */}
          {!editingId && PROGRAM_CATEGORIES.map((cat, i) => (
            dialogTab === 3 + i && (
              <Stack key={cat.formKey} spacing={1} mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Choose the {cat.label.toLowerCase()} available to this company.
                </Typography>
                <ProgramDataPicker
                  label={cat.label}
                  options={programOptions[cat.optionsKey]}
                  value={form[cat.formKey]}
                  onChange={(ids) => setField(cat.formKey, ids)}
                  loading={programOptionsLoading}
                />
              </Stack>
            )
          ))}

          {/* ---- Summary: review + create (create mode only) ---- */}
          {!editingId && dialogTab === SUMMARY_TAB && (
            <Stack spacing={2} mt={2}>
              <Typography variant="body2" color="text.secondary">
                Review the details below, then create the company.
              </Typography>

              <Box sx={{ p: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.06), borderRadius: 1 }}>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Company:</strong> {form.company_name.trim() || <em>(not set)</em>}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Admin user:</strong>{' '}
                    {[form.first_name.trim(), form.last_name.trim()].filter(Boolean).join(' ') || <em>(not set)</em>}
                    {form.email_address.trim() ? ` — ${form.email_address.trim()}` : ''}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Onboarding:</strong> {ONBOARDING_LABELS[form.onboarding_mode] ?? form.onboarding_mode}
                  </Typography>
                  {form.onboarding_mode !== 'internal' && (
                    <Typography variant="body2">
                      <strong>Plan:</strong>{' '}
                      {PLAN_LABELS[form.plan_type === 0 ? 1 : form.plan_type] ?? `Tier ${form.plan_type}`}
                    </Typography>
                  )}
                  {form.onboarding_mode === 'free_trial' && (
                    <Typography variant="body2">
                      <strong>Trial:</strong> {form.trial_days} days
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Program data selected
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {PROGRAM_CATEGORIES.map((cat) => (
                    <Chip
                      key={cat.formKey}
                      size="small"
                      label={`${cat.label}: ${form[cat.formKey].length}`}
                    />
                  ))}
                </Box>
              </Box>

              {/* Pay-first card entry lives HERE, on the same step as the Create
                  button, so the Stripe iframe is mounted when handleSave tokenises. */}
              {form.onboarding_mode === 'pay_first' && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Payment
                  </Typography>
                  {stripePromise ? (
                    <Box sx={{ mt: 1 }}>
                      <Elements stripe={stripePromise}>
                        <CardSection ref={cardSectionRef} />
                      </Elements>
                    </Box>
                  ) : (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Stripe is not configured (no publishable key). Card entry is unavailable —
                      set STRIPE_PUBLISHABLE_KEY on the API, or use Free trial / Internal mode.
                    </Alert>
                  )}
                </Box>
              )}

              <Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => void handleSave()}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                  {saving ? 'Creating…' : 'Create Company'}
                </Button>
              </Box>
            </Stack>
          )}

          {/* ---- Tab 3: Reset Password (or create admin when none exists) ---- */}
          {dialogTab === 3 && editingId && (
            adminEmail ? (
              <Stack spacing={2} mt={2}>
                <Typography variant="body2" color="text.secondary">
                  Set a new login password for this company&apos;s admin user. The password is written
                  directly to Supabase Auth — the user is not emailed.
                </Typography>
                <Alert severity="info">
                  Resetting password for <strong>{adminEmail}</strong>
                </Alert>
                <TextField
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth size="small"
                  autoComplete="new-password"
                  helperText="At least 8 characters"
                  disabled={resettingPw}
                />
                <TextField
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth size="small"
                  autoComplete="new-password"
                  disabled={resettingPw}
                />
                <Box>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => void handleResetPassword()}
                    disabled={resettingPw || newPassword.length < 8 || newPassword !== confirmPassword}
                    startIcon={resettingPw ? <CircularProgress size={16} /> : undefined}
                  >
                    {resettingPw ? 'Resetting…' : 'Reset Password'}
                  </Button>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Typography variant="body2" color="text.secondary">
                  Or force the admin to choose their own new password the next time they log in.
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => void handleRequirePasswordReset()}
                    disabled={requiringReset}
                    startIcon={requiringReset ? <CircularProgress size={16} /> : undefined}
                  >
                    {requiringReset ? 'Applying…' : 'Require password change on next login'}
                  </Button>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={2} mt={2}>
                <Alert severity="warning">
                  No admin user (role <code>companies_admin</code>) exists for this company. Create one
                  below — it&apos;s added to Supabase Auth and can log in immediately.
                  <br /><br />
                  <strong>Migrated account?</strong> If the email already exists in Auth, the existing
                  user will be adopted: their role is updated to <code>companies_admin</code>, their
                  password is set to the one you enter, and they can log in straight away.
                </Alert>
                <TextField
                  label="Admin Email"
                  type="email"
                  value={createAdminEmail}
                  onChange={(e) => setCreateAdminEmail(e.target.value)}
                  fullWidth size="small"
                  autoComplete="off"
                  disabled={creatingAdmin}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="First Name"
                    value={createAdminFirst}
                    onChange={(e) => setCreateAdminFirst(e.target.value)}
                    fullWidth size="small"
                    disabled={creatingAdmin}
                  />
                  <TextField
                    label="Last Name"
                    value={createAdminLast}
                    onChange={(e) => setCreateAdminLast(e.target.value)}
                    fullWidth size="small"
                    disabled={creatingAdmin}
                  />
                </Stack>
                <TextField
                  label="Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth size="small"
                  autoComplete="new-password"
                  helperText="At least 8 characters"
                  disabled={creatingAdmin}
                />
                <TextField
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth size="small"
                  autoComplete="new-password"
                  disabled={creatingAdmin}
                />
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => void handleCreateAdmin()}
                    disabled={creatingAdmin || !createAdminEmail.trim() || newPassword.length < 8 || newPassword !== confirmPassword}
                    startIcon={creatingAdmin ? <CircularProgress size={16} /> : undefined}
                  >
                    {creatingAdmin ? 'Creating…' : 'Create Admin User'}
                  </Button>
                </Box>
              </Stack>
            )
          )}

          {/* ---- Tab 4: Agronomists (edit mode) ---- */}
          {dialogTab === 4 && editingId && (
            <Stack spacing={2} mt={2}>
              <Typography variant="body2" color="text.secondary">
                Set how many <strong>agronomist</strong> users this company may add. The company&apos;s
                user management screen blocks new agronomist accounts once this allocation is reached.
                Choose <strong>None</strong> to disable agronomist accounts entirely.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Agronomists allowed</InputLabel>
                <Select
                  label="Agronomists allowed"
                  value={form.agronomist}
                  onChange={(e) => setField('agronomist', Number(e.target.value))}
                >
                  {AGRONOMIST_SEAT_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n === 0 ? 'None' : n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Alert severity="info">
                Allocation is saved with the company when you click <strong>Update</strong>.
              </Alert>
            </Stack>
          )}

          {/* Audit info */}
          {editingId && auditInfo && (
            <Box
              mt={3}
              p={2}
              sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                Audit Trail
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(auditInfo.created_at)}{auditInfo.created_by ? <> &mdash; <strong>{auditInfo.created_by}</strong></> : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(auditInfo.updated_at)}{auditInfo.updated_by ? <> &mdash; <strong>{auditInfo.updated_by}</strong></> : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>{editingId && dialogTab === 3 ? 'Close' : 'Cancel'}</Button>

          {/* Create mode is a wizard: Back/Next step through tabs without validating.
              Validation + creation happen only on the final Summary tab, via its own
              Create Company button. */}
          {!editingId && dialogTab > 0 && dialogTab !== SUMMARY_TAB && (
            <Button
              onClick={() => { setFormError(null); setDialogTab((t) => Math.max(t - 1, 0)); }}
              disabled={saving}
            >
              Back
            </Button>
          )}
          {!editingId && dialogTab !== SUMMARY_TAB && (
            <Button
              variant="contained"
              onClick={() => { setFormError(null); setDialogTab((t) => Math.min(t + 1, SUMMARY_TAB)); }}
            >
              Next
            </Button>
          )}

          {/* Edit mode keeps the single Update action (hidden on the Reset Password tab). */}
          {editingId && dialogTab !== 3 && (
            <Button
              variant="contained"
              onClick={() => void handleSave()}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {saving ? 'Saving…' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* -----------------------------------------------------------------------
          Delete confirmation
      ----------------------------------------------------------------------- */}
      <Dialog open={deleteId !== null} onClose={() => !deleting && setDeleteId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Company — {deleteLabel}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <p>You are about to delete <strong>{deleteLabel}</strong>. The following actions will happen immediately:</p>
            <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
              <li><strong>Stripe billing stopped</strong> — every active plan/trial on this Stripe customer is cancelled immediately, so no further fees are charged. The customer record itself is retained in Stripe so the company can be restored.</li>
              <li><strong>All users banned</strong> — no one from this company will be able to log in.</li>
              <li><strong>Record soft-deleted</strong> — data is retained and can be restored by an administrator if needed.</li>
            </ul>
            <p style={{ marginTop: 8 }}>This action can be undone using the <strong>Restore</strong> option on the Deleted tab. However, the Stripe subscription will need to be manually re-created after restoring.</p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -----------------------------------------------------------------------
          Restore confirmation
      ----------------------------------------------------------------------- */}
      <Dialog open={restoreId !== null} onClose={() => !restoring && setRestoreId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Restore Company — {restoreLabel}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <p>You are about to restore <strong>{restoreLabel}</strong>. The following actions will happen immediately:</p>
            <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
              <li><strong>Stripe subscription reactivated</strong> — a new subscription will be created using the company&apos;s existing payment method and plan.</li>
              <li><strong>All users unbanned</strong> — company users will be able to log in again immediately.</li>
              <li><strong>Record restored</strong> — the company will reappear on the Active tab.</li>
            </ul>
            <p style={{ marginTop: 8 }}>If Stripe reactivation fails (e.g. expired payment method), the company will still be restored but billing will need to be set up manually in the Stripe dashboard.</p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreId(null)} disabled={restoring}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => void handleRestore()}
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={16} /> : undefined}
          >
            {restoring ? 'Restoring…' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -----------------------------------------------------------------------
          Permanent-delete (purge) confirmation
      ----------------------------------------------------------------------- */}
      <Dialog open={purgeId !== null} onClose={() => !purging && setPurgeId(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Permanently delete — {purgeLabel}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will remove <strong>all data</strong> related to this company. No data can be retrieved.
          </Alert>
          <DialogContentText component="div">
            <p>Permanently deleted, with no restore:</p>
            <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
              <li>All blocks, tasks, program data (crop types, chemicals, fertilizers, etc.), observations and reports.</li>
              <li>All company users (removed from authentication).</li>
              <li>The Stripe customer, including any subscriptions and billing history.</li>
              <li>The company record itself.</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeId(null)} disabled={purging}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handlePurge()}
            disabled={purging}
            startIcon={purging ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
          >
            {purging ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
